import { TEMPLATES } from './conventionTemplates';
import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import WordImporter from './WordImporter';
import { sanitizeHtml } from '../utils/sanitizeHtml';
import {
  Bold,
  Italic,
  Underline,
  List,
  ListOrdered,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Heading1,
  Heading2,
  Heading3,
  Link,
  Image,
  Table,
  Quote,
  Minus,
  Eye,
  Code,
  Maximize2,
  Minimize2,
  Copy,
  Undo,
  Redo,
  FileText,
  RefreshCw,
  Zap
} from 'lucide-react';
import { toast } from 'sonner';

// Bouton de la barre d'outils
const ToolbarButton = ({ icon: Icon, label, onClick, active = false, className = '' }) => (
  <Button
    type="button"
    variant={active ? "secondary" : "ghost"}
    size="sm"
    className={`h-8 w-8 p-0 ${active ? 'bg-slate-200' : ''} ${className}`}
    onClick={onClick}
    title={label}
  >
    <Icon className="h-4 w-4" />
  </Button>
);

// Séparateur de la barre d'outils
const ToolbarSeparator = () => (
  <div className="w-px h-6 bg-slate-300 mx-1" />
);

export default function ConventionEditor({ 
  value, 
  onChange, 
  type: type = 'cooper',
  height = '500px'
}) {
  const [viewMode, setViewMode] = useState('split'); // 'edit', 'preview', 'split'
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [history, setHistory] = useState([value || '']);
  const [historyIndex, setHistoryIndex] = useState(0);
  const [syncScroll, setSyncScroll] = useState(true);
  const [lastWordFile, setLastWordFile] = useState(null);
  const textareaRef = useRef(null);
  const previewRef = useRef(null);
  const isScrolling = useRef(false);
  
  // Aperçu INSTANTANÉ - sans debounce pour une mise à jour en temps réel
  const previewContent = useMemo(() => value || '', [value]);
  
  // Synchronisation du scroll
  const handleEditorScroll = useCallback((e) => {
    if (!syncScroll || isScrolling.current) return;
    const editor = e.target;
    const preview = previewRef.current;
    if (!preview) return;
    
    isScrolling.current = true;
    const scrollRatio = editor.scrollTop / (editor.scrollHeight - editor.clientHeight);
    preview.scrollTop = scrollRatio * (preview.scrollHeight - preview.clientHeight);
    setTimeout(() => { isScrolling.current = false; }, 50);
  }, [syncScroll]);
  
  const handlePreviewScroll = useCallback((e) => {
    if (!syncScroll || isScrolling.current) return;
    const preview = e.target;
    const editor = textareaRef.current;
    if (!editor) return;
    
    isScrolling.current = true;
    const scrollRatio = preview.scrollTop / (preview.scrollHeight - preview.clientHeight);
    editor.scrollTop = scrollRatio * (editor.scrollHeight - editor.clientHeight);
    setTimeout(() => { isScrolling.current = false; }, 50);
  }, [syncScroll]);
  
  // Gestion de l'historique (Undo/Redo)
  const pushToHistory = useCallback((newValue) => {
    setHistory(prev => {
      const newHistory = prev.slice(0, historyIndex + 1);
      newHistory.push(newValue);
      if (newHistory.length > 50) newHistory.shift();
      return newHistory;
    });
    setHistoryIndex(prev => Math.min(prev + 1, 49));
  }, [historyIndex]);
  
  const handleUndo = useCallback(() => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      setHistoryIndex(newIndex);
      onChange(history[newIndex]);
    }
  }, [history, historyIndex, onChange]);
  
  const handleRedo = useCallback(() => {
    if (historyIndex < history.length - 1) {
      const newIndex = historyIndex + 1;
      setHistoryIndex(newIndex);
      onChange(history[newIndex]);
    }
  }, [history, historyIndex, onChange]);
  
  // Insertion de texte/balises HTML
  const insertTag = useCallback((tagStart, tagEnd = '') => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = (value || '').substring(start, end);
    const newText = (value || '').substring(0, start) + tagStart + selectedText + tagEnd + (value || '').substring(end);
    
    onChange(newText);
    pushToHistory(newText);
    
    // Repositionner le curseur
    setTimeout(() => {
      textarea.focus();
      const newPos = start + tagStart.length + selectedText.length;
      textarea.setSelectionRange(newPos, newPos);
    }, 0);
  }, [value, onChange, pushToHistory]);
  
  // Gestion des modifications en temps réel
  const handleChange = useCallback((e) => {
    const newValue = e.target.value;
    onChange(newValue);
    
    // Ajouter à l'historique après un court délai (pour regrouper les frappes rapides)
    clearTimeout(window.editorHistoryTimeout);
    window.editorHistoryTimeout = setTimeout(() => {
      pushToHistory(newValue);
    }, 500);
  }, [onChange, pushToHistory]);
  
  // Actions de la barre d'outils
  const toolbarActions = {
    bold: () => insertTag('<strong>', '</strong>'),
    italic: () => insertTag('<em>', '</em>'),
    underline: () => insertTag('<u>', '</u>'),
    h1: () => insertTag('<h1 style="color: #1e40af; margin-bottom: 15px;">', '</h1>\n'),
    h2: () => insertTag('<h2 style="color: #3b82f6; margin-bottom: 12px;">', '</h2>\n'),
    h3: () => insertTag('<h3 style="color: #1e40af; border-bottom: 2px solid #3b82f6; padding-bottom: 5px; margin-bottom: 10px;">', '</h3>\n'),
    ul: () => insertTag('<ul style="margin-bottom: 15px;">\n  <li>', '</li>\n  <li>Élément 2</li>\n</ul>\n'),
    ol: () => insertTag('<ol style="margin-bottom: 15px;">\n  <li>', '</li>\n  <li>Élément 2</li>\n</ol>\n'),
    alignLeft: () => insertTag('<div style="text-align: left;">', '</div>\n'),
    alignCenter: () => insertTag('<div style="text-align: center;">', '</div>\n'),
    alignRight: () => insertTag('<div style="text-align: right;">', '</div>\n'),
    link: () => {
      const url = prompt('URL du lien:');
      if (url) insertTag(`<a href="${url}" style="color: #3b82f6; text-decoration: underline;">`, '</a>');
    },
    image: () => {
      const url = prompt("URL de l'image:");
      if (url) insertTag(`<img src="${url}" style="max-width: 100%; height: auto;" alt="Image" />`, '');
    },
    table: () => insertTag(`<table style="width: 100%; border-collapse: collapse; margin-bottom: 15px;">
  <thead>
    <tr style="background-color: #f1f5f9;">
      <th style="border: 1px solid #e2e8f0; padding: 8px;">Colonne 1</th>
      <th style="border: 1px solid #e2e8f0; padding: 8px;">Colonne 2</th>
    </tr>
  </thead>
  <tbody>
    <tr>
      <td style="border: 1px solid #e2e8f0; padding: 8px;">`, `</td>
      <td style="border: 1px solid #e2e8f0; padding: 8px;">Donnée 2</td>
    </tr>
  </tbody>
</table>\n`),
    quote: () => insertTag('<blockquote style="border-left: 4px solid #3b82f6; padding-left: 15px; margin: 15px 0; font-style: italic; color: #64748b;">', '</blockquote>\n'),
    hr: () => insertTag('<hr style="border: none; border-top: 2px solid #e2e8f0; margin: 20px 0;" />\n', ''),
  };
  
  // Appliquer un template
  const applyTemplate = (templateKey) => {
    if (value && value.trim() && !window.confirm('Le contenu actuel sera remplacé. Continuer ?')) {
      return;
    }
    const template = TEMPLATES[templateKey];
    if (template) {
      onChange(template.content);
      pushToHistory(template.content);
      toast.success(`Modèle "${template.name}" appliqué`);
    }
  };
  
  // Import Word avec mise à jour instantanée
  const handleWordImport = useCallback((htmlContent, fileName) => {
    onChange(htmlContent);
    pushToHistory(htmlContent);
    setLastWordFile(fileName);
    toast.success(`✅ Contenu importé depuis "${fileName}" - Modifiez le HTML, l'aperçu se met à jour instantanément !`);
  }, [onChange, pushToHistory]);
  
  // Recharger le dernier fichier Word
  const _reloadWordFile = () => {
    if (lastWordFile) {
      toast.info(`Utilisez le bouton "Importer Word" pour recharger un fichier`);
    }
  };
  
  // Copier le HTML
  const handleCopyHtml = () => {
    navigator.clipboard.writeText(value || '');
    toast.success('HTML copié dans le presse-papier');
  };
  
  // Raccourcis clavier
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!textareaRef.current || document.activeElement !== textareaRef.current) return;
      
      if (e.ctrlKey || e.metaKey) {
        switch (e.key.toLowerCase()) {
          case 'b':
            e.preventDefault();
            toolbarActions.bold();
            break;
          case 'i':
            e.preventDefault();
            toolbarActions.italic();
            break;
          case 'u':
            e.preventDefault();
            toolbarActions.underline();
            break;
          case 'z':
            e.preventDefault();
            if (e.shiftKey) handleRedo();
            else handleUndo();
            break;
          case 'y':
            e.preventDefault();
            handleRedo();
            break;
          default:
            break;
        }
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [toolbarActions, handleUndo, handleRedo]);
  
  const containerStyle = isFullscreen ? {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 9999,
    backgroundColor: 'white',
    padding: '20px',
    overflow: 'auto'
  } : {};

  // Stats en temps réel
  const stats = useMemo(() => ({
    chars: (value || '').length,
    tags: ((value || '').match(/<[^>]+>/g) || []).length,
    pages: Math.ceil((value || '').length / 1500)
  }), [value]);

  return (
    <div style={containerStyle}>
      <Card className="border-2">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <FileText className="h-5 w-5 text-blue-600" />
              Éditeur de Convention
              <Badge className="ml-2 text-xs bg-green-700 text-white border-green-800 font-semibold">
                <Zap className="h-3 w-3 mr-1" />
                Temps réel
              </Badge>
            </CardTitle>
            <div className="flex items-center gap-2 flex-wrap">
              {/* Templates */}
              <select
                className="text-sm border rounded px-2 py-1 bg-white"
                onChange={(e) => e.target.value && applyTemplate(e.target.value)}
                value=""
              >
                <option value="">📄 Modèles...</option>
                <option value="cooper">Convention COOPER&apos;S</option>
                <option value="expert">Convention EXPERT</option>
                <option value="autre">Convention Personnalisée</option>
              </select>
              
              {/* Import Word */}
              <WordImporter 
                onImport={handleWordImport}
                buttonVariant="outline"
                buttonText="Importer Word"
              />
              
              {/* Indicateur fichier Word */}
              {lastWordFile && (
                <Badge variant="secondary" className="text-xs">
                  📄 {lastWordFile}
                </Badge>
              )}
              
              {/* Mode de vue */}
              <div className="flex border rounded overflow-hidden">
                <Button
                  variant={viewMode === 'edit' ? 'secondary' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('edit')}
                  className="rounded-none"
                  title="Code HTML uniquement"
                >
                  <Code className="h-4 w-4" />
                </Button>
                <Button
                  variant={viewMode === 'split' ? 'secondary' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('split')}
                  className="rounded-none border-x"
                  title="Vue partagée (recommandé)"
                >
                  <div className="flex">
                    <div className="w-2 h-4 border-r border-current" />
                    <div className="w-2 h-4" />
                  </div>
                </Button>
                <Button
                  variant={viewMode === 'preview' ? 'secondary' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('preview')}
                  className="rounded-none"
                  title="Aperçu uniquement"
                >
                  <Eye className="h-4 w-4" />
                </Button>
              </div>
              
              {/* Sync scroll toggle */}
              {viewMode === 'split' && (
                <Button
                  variant={syncScroll ? 'secondary' : 'ghost'}
                  size="sm"
                  onClick={() => setSyncScroll(!syncScroll)}
                  title={syncScroll ? 'Sync scroll activé' : 'Sync scroll désactivé'}
                >
                  <RefreshCw className={`h-4 w-4 ${syncScroll ? 'text-blue-600' : ''}`} />
                </Button>
              )}
              
              {/* Fullscreen */}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsFullscreen(!isFullscreen)}
                title={isFullscreen ? 'Quitter plein écran' : 'Plein écran'}
              >
                {isFullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
              </Button>
            </div>
          </div>
        </CardHeader>
        
        <CardContent>
          {/* Barre d'outils */}
          {viewMode !== 'preview' && (
            <div className="flex flex-wrap items-center gap-1 p-2 bg-slate-50 rounded-lg mb-3 border">
              {/* Undo/Redo */}
              <ToolbarButton icon={Undo} label="Annuler (Ctrl+Z)" onClick={handleUndo} />
              <ToolbarButton icon={Redo} label="Rétablir (Ctrl+Y)" onClick={handleRedo} />
              <ToolbarSeparator />
              
              {/* Titres */}
              <ToolbarButton icon={Heading1} label="Titre 1" onClick={toolbarActions.h1} />
              <ToolbarButton icon={Heading2} label="Titre 2" onClick={toolbarActions.h2} />
              <ToolbarButton icon={Heading3} label="Titre 3" onClick={toolbarActions.h3} />
              <ToolbarSeparator />
              
              {/* Formatage texte */}
              <ToolbarButton icon={Bold} label="Gras (Ctrl+B)" onClick={toolbarActions.bold} />
              <ToolbarButton icon={Italic} label="Italique (Ctrl+I)" onClick={toolbarActions.italic} />
              <ToolbarButton icon={Underline} label="Souligné (Ctrl+U)" onClick={toolbarActions.underline} />
              <ToolbarSeparator />
              
              {/* Alignement */}
              <ToolbarButton icon={AlignLeft} label="Aligner à gauche" onClick={toolbarActions.alignLeft} />
              <ToolbarButton icon={AlignCenter} label="Centrer" onClick={toolbarActions.alignCenter} />
              <ToolbarButton icon={AlignRight} label="Aligner à droite" onClick={toolbarActions.alignRight} />
              <ToolbarSeparator />
              
              {/* Listes */}
              <ToolbarButton icon={List} label="Liste à puces" onClick={toolbarActions.ul} />
              <ToolbarButton icon={ListOrdered} label="Liste numérotée" onClick={toolbarActions.ol} />
              <ToolbarSeparator />
              
              {/* Éléments */}
              <ToolbarButton icon={Link} label="Lien" onClick={toolbarActions.link} />
              <ToolbarButton icon={Image} label="Image" onClick={toolbarActions.image} />
              <ToolbarButton icon={Table} label="Tableau" onClick={toolbarActions.table} />
              <ToolbarButton icon={Quote} label="Citation" onClick={toolbarActions.quote} />
              <ToolbarButton icon={Minus} label="Ligne horizontale" onClick={toolbarActions.hr} />
              <ToolbarSeparator />
              
              {/* Copier */}
              <ToolbarButton icon={Copy} label="Copier HTML" onClick={handleCopyHtml} />
            </div>
          )}
          
          {/* Zone d'édition */}
          <div 
            className={`grid gap-4 ${viewMode === 'split' ? 'grid-cols-2' : 'grid-cols-1'}`}
            style={{ minHeight: height }}
          >
            {/* Éditeur HTML */}
            {viewMode !== 'preview' && (
              <div className="flex flex-col">
                <Label className="mb-2 font-semibold flex items-center gap-2">
                  <Code className="h-4 w-4" />
                  Code HTML
                  <span className="text-xs text-slate-500 font-normal ml-2">
                    (raccourcis: Ctrl+B, Ctrl+I, Ctrl+U, Ctrl+Z)
                  </span>
                </Label>
                <Textarea
                  ref={textareaRef}
                  value={value || ''}
                  onChange={handleChange}
                  onScroll={handleEditorScroll}
                  className="flex-1 font-mono text-sm resize-none"
                  style={{ minHeight: height }}
                  placeholder="Tapez votre code HTML ici ou importez un document Word...&#10;&#10;L'aperçu se met à jour instantanément à chaque modification !"
                />
              </div>
            )}
            
            {/* Aperçu INSTANTANÉ */}
            {(viewMode === 'preview' || viewMode === 'split') && (
              <div className="flex flex-col">
                <Label className="mb-2 font-semibold flex items-center gap-2">
                  <Eye className="h-4 w-4" />
                  Aperçu instantané
                  <Badge className="ml-2 text-xs bg-green-700 text-white border-green-800 font-semibold">
                    <Zap className="h-3 w-3 mr-1" />
                    LIVE
                  </Badge>
                  {syncScroll && viewMode === 'split' && (
                    <Badge className="ml-1 text-xs bg-blue-700 text-white border-blue-800 font-semibold">
                      <RefreshCw className="h-3 w-3 mr-1" />
                      Sync
                    </Badge>
                  )}
                </Label>
                <div
                  ref={previewRef}
                  onScroll={handlePreviewScroll}
                  className="flex-1 border rounded-lg p-4 bg-white overflow-auto prose prose-sm max-w-none shadow-inner"
                  style={{ minHeight: height }}
                  dangerouslySetInnerHTML={{ 
                    __html: sanitizeHtml(previewContent || '<p class="text-gray-400 italic">L\'aperçu apparaîtra ici dès que vous commencerez à taper...</p>')
                  }}
                />
              </div>
            )}
          </div>
          
          {/* Statistiques en temps réel */}
          <div className="mt-3 flex items-center justify-between text-xs text-slate-500 border-t pt-2">
            <div className="flex gap-4">
              <span>📝 {stats.chars.toLocaleString()} caractères</span>
              <span>🏷️ {stats.tags} balises HTML</span>
              <span>📄 ~{stats.pages} page(s) A4</span>
            </div>
            <div className="flex items-center gap-2">
              {lastWordFile && (
                <span className="text-blue-600">📎 Importé depuis: {lastWordFile}</span>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
